import heic2any from "heic2any";

export const isHeicFile = (file: File | Blob, fileName?: string): boolean => {
  if (!file) {
    return false;
  }

  const resolvedName = fileName || (file instanceof File ? file.name : "");
  const normalizedType = file.type ? file.type.toLowerCase().trim() : "";
  const isHeicMime =
    normalizedType === "image/heic" ||
    normalizedType === "image/heif" ||
    normalizedType === "image/heic-sequence" ||
    normalizedType === "image/heif-sequence";

  if (isHeicMime) {
    return true;
  }

  const isHeicExtension = /\.(heic|heif)$/i.test(resolvedName);
  return isHeicExtension;
};

export const resizeImageIfTooLarge = async (
  file: File,
  maxDimension: number = 1200
): Promise<File> => {
  if (!file || !(file instanceof File)) return file;

  return new Promise((resolve) => {
    try {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const { width, height } = img;

        let newWidth = width;
        let newHeight = height;
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            newWidth = maxDimension;
            newHeight = Math.round((height * maxDimension) / width);
          } else {
            newHeight = maxDimension;
            newWidth = Math.round((width * maxDimension) / height);
          }
        }

        const canvas = document.createElement("canvas");
        canvas.width = newWidth;
        canvas.height = newHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(file);
          return;
        }

        ctx.drawImage(img, 0, 0, newWidth, newHeight);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            const baseName = file.name ? file.name.replace(/\.[^/.]+$/, "") : "image";
            const resizedFile = new File([blob], `${baseName}.png`, {
              type: "image/png",
              lastModified: Date.now(),
            });
            resolve(resizedFile);
          },
          "image/png"
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(file);
      };

      img.src = url;
    } catch {
      resolve(file);
    }
  });
};

export const convertHeicToPng = async (file: File): Promise<File> => {
  if (!file) {
    return file;
  }

  if (!isHeicFile(file)) {
    return await resizeImageIfTooLarge(file);
  }

  try {
    const conversionResult = await heic2any({
      blob: file,
      toType: "image/png",
      quality: 0.9,
    });

    const pngBlob = Array.isArray(conversionResult)
      ? conversionResult[0]
      : conversionResult;

    const baseName = file.name ? file.name.replace(/\.(heic|heif)$/i, "") : "image";
    const newFileName = `${baseName}.png`;

    const pngFile = new File([pngBlob], newFileName, {
      type: "image/png",
      lastModified: Date.now(),
    });

    return await resizeImageIfTooLarge(pngFile);
  } catch (error) {
    console.error("HEIC to PNG conversion failed:", error);
    return file;
  }
};

export const fileToDataUrl = (file: File | Blob): Promise<string> => {
  return new Promise((resolve) => {
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(typeof reader.result === "string" ? reader.result : "");
      };
      reader.onerror = () => resolve("");
      reader.readAsDataURL(file);
    } catch {
      resolve("");
    }
  });
};
